import { useState, useCallback } from 'react';
import { config } from '../config';
import { useAuth } from '../contexts/AuthContext';

const SIMULATOR_API_URL = config.SIMULATOR_API_ENDPOINT || '';

const useSimulatorAPI = () => {
  const { getAuthToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const callSimAPI = useCallback(async (path, options = {}) => {
    const { method = 'GET', body = null } = options;
    setLoading(true);
    setError(null);

    try {
      const token = await getAuthToken();
      const url = `${SIMULATOR_API_URL}${path}`;
      const fetchOptions = {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token,
        },
      };
      if (body) {
        fetchOptions.body = JSON.stringify(body);
      }

      const response = await fetch(url, fetchOptions);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `Request failed: ${response.status}`);
      }

      setLoading(false);
      return data;
    } catch (err) {
      const errorMessage = err.message || 'Simulator API request failed';
      setError(errorMessage);
      setLoading(false);
      throw err;
    }
  }, [getAuthToken]);

  const seedRestaurant = useCallback(async (sections = null) => {
    const body = sections ? { sections } : null;
    return callSimAPI('/sim/seed', { method: 'POST', body });
  }, [callSimAPI]);

  const simulateDay = useCallback(async (businessDate) => {
    return callSimAPI('/sim/daily', {
      method: 'POST',
      body: { business_date: businessDate },
    });
  }, [callSimAPI]);

  const getStatus = useCallback(async () => {
    return callSimAPI('/sim/status');
  }, [callSimAPI]);

  const getParams = useCallback(async () => {
    return callSimAPI('/sim/params');
  }, [callSimAPI]);

  const updateParams = useCallback(async (params) => {
    return callSimAPI('/sim/params', { method: 'PUT', body: params });
  }, [callSimAPI]);

  const callSimToastAPI = useCallback(async (path, queryParams = {}) => {
    const qs = new URLSearchParams(queryParams).toString();
    const fullPath = qs ? `${path}?${qs}` : path;
    return callSimAPI(fullPath);
  }, [callSimAPI]);

  return {
    loading,
    error,
    simulatorUrl: SIMULATOR_API_URL,
    seedRestaurant,
    simulateDay,
    getStatus,
    getParams,
    updateParams,
    callSimToastAPI,
    callSimAPI,
  };
};

export default useSimulatorAPI;
